import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { CartHeader } from "@/components/cart/CartHeader";
import { CartItems } from "@/components/cart/CartItems";
import { CartSummary } from "@/components/cart/CartSummary";
import { CartActions } from "@/components/cart/CartActions";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { API, money } from "@/lib/site";

const Input = ({ label, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400">{label}</span>
    <input
      {...props}
      className="w-full border border-line bg-surface px-4 py-3 text-sm text-neutral-100 outline-none placeholder:text-neutral-600 focus:border-regal-amber transition-colors"
    />
  </label>
);

export default function CartDrawer() {
  const { items, open, setOpen, setQty, removeItem, subtotal, vat, total, clear } = useCart();
  const ccy = items[0]?.currency || "GBP";
  const [step, setStep] = useState("cart");
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", country: "", notes: "" });
  const [ppConfig, setPpConfig] = useState(null); // null = not probed, false = not configured
  const [stripeConfig, setStripeConfig] = useState(null); // null = not probed, false = not configured

  useEffect(() => {
    if (!open) return;
    if (ppConfig === null) axios.get(`${API}/paypal/config`).then(({ data }) => setPpConfig(data)).catch(() => setPpConfig(false));
    if (stripeConfig === null) axios.get(`${API}/stripe/config`).then(({ data }) => setStripeConfig(data)).catch(() => setStripeConfig(false));
  }, [open, ppConfig, stripeConfig]);

  const close = () => { setOpen(false); setTimeout(() => { if (order) { setStep("cart"); setOrder(null); } }, 400); };

  const cartItemsPayload = () => items.map((i) => ({ slug: i.slug, qty: i.qty, meta: i.meta || undefined }));
  const formValid = form.name.trim() && /.+@.+\..+/.test(form.email) && form.country.trim();

  const checkout = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/orders`, { customer: form, items: cartItemsPayload() });
      setOrder(data);
      setStep("done");
      clear();
    } catch (err) {
      toast.error("Checkout failed", { description: "Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const stripeCheckout = async () => {
    if (!formValid) {
      toast.error("Details needed", { description: "Name, email and country are required before payment." });
      return;
    }
    try {
      setBusy(true);
      const { data } = await axios.post(`${API}/stripe/create-checkout`, {
        origin_url: window.location.origin,
        customer: form,
        items: cartItemsPayload(),
      });
      window.location.href = data.checkout_url;
    } catch {
      setBusy(false);
      toast.error("Card payment unavailable", { description: "Please try PayPal or place the order without payment." });
    }
  };

  const paypalCreate = async () => {
    const { data } = await axios.post(`${API}/paypal/create-order`, { customer: form, items: cartItemsPayload() });
    return data.paypal_order_id;
  };
  const paypalCapture = async (data) => {
    try {
      setBusy(true);
      const res = await axios.post(`${API}/paypal/capture-order`, { paypal_order_id: data.orderID });
      setOrder(res.data);
      setStep("done");
      clear();
    } catch {
      toast.error("Payment capture failed", { description: "If you were charged, contact us with your PayPal receipt." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="overlay"
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close}
            data-testid="cart-overlay"
          />
          <motion.aside
            key="drawer"
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col border-l border-line bg-card"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <CartHeader step={step} close={close} />

            {step === "cart" && (
              <>
                <CartItems items={items} close={close} removeItem={removeItem} setQty={setQty} />
                <CartSummary items={items} subtotal={subtotal} total={total} currency={ccy} next={() => setStep("form")} paypalAvailable={ppConfig} />
              </>
            )}

            {step === "form" && (
              <form onSubmit={checkout} className="flex flex-1 flex-col overflow-y-auto px-6 py-5" data-testid="checkout-form">
                <div className="space-y-4">
                  <Input label="Full name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="checkout-name-input" />
                  <Input label="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="checkout-email-input" />
                  <Input label="Company (optional)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="checkout-company-input" />
                  <Input label="Country" required value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="checkout-country-input" />
                  <label className="block">
                    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400">Notes (optional)</span>
                    <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="checkout-notes-input" className="w-full border border-line bg-surface px-4 py-3 text-sm text-neutral-100 outline-none focus:border-regal-amber transition-colors" />
                  </label>
                </div>
                <div className="mt-auto pt-6 space-y-3">
                  <CartActions ppConfig={ppConfig} stripeConfig={stripeConfig} busy={busy} total={total} currency={ccy} formValid={formValid} paypalCreate={paypalCreate} paypalCapture={paypalCapture} stripeCheckout={stripeCheckout} />
                  <button type="button" onClick={() => setStep("cart")} data-testid="checkout-back-button" className="w-full border border-line py-3.5 font-display text-xs font-bold uppercase tracking-[0.16em] text-neutral-300 hover:border-regal-amber hover:text-regal-amber transition-colors">
                    Back to cart
                  </button>
                </div>
              </form>
            )}

            {step === "done" && order && (
              <div className="flex flex-1 flex-col items-center justify-center gap-5 px-8 text-center" data-testid="checkout-success">
                <CheckCircle2 size={52} className="text-regal-amber" />
                <div>
                  <p className="font-display text-xl font-bold uppercase tracking-wide">Order placed</p>
                  <p className="mt-2 text-sm text-neutral-400">Reference <span className="font-mono text-regal-amber" data-testid="order-reference">{order.reference}</span></p>
                  <p className="mt-1 text-sm text-neutral-400">Total {money(order.total_inc_vat, order.items?.[0]?.currency || "GBP")}</p>
                  <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                    {order.payment === "paypal" ? "Paid via PayPal — confirmation emailed" : order.payment === "stripe" ? "Paid by card — confirmation emailed" : "Demo order — payment was simulated"}
                  </p>
                </div>
                <button onClick={close} data-testid="checkout-done-button" className="border border-regal-amber px-8 py-3.5 font-display text-xs font-bold uppercase tracking-[0.16em] text-regal-amber hover:bg-regal-amber hover:text-void transition-colors">
                  Continue
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
